"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { ModuleSettingsStore } from "../../../../utils/contentstack/moduleSettingsStore.js";
import { useRef, useMemo, useState, useReducer, useEffect } from "react";
import { Popup, Notification, Button, AnnouncerModel } from "@nxt-costco-com/forge-components";
import { FontLineHeightMd, SpaceSm } from "@nxt-costco-com/forge-design-tokens";
import { CompareProductTile } from "../CompareProductTile/index.js";
import { ALL_ITEMS_REMOVED_ANNOUNCER_TEXT_KEY, COMPARE_BUTTON_TEXT_KEY, EMPTY_SLOT_TEXT_KEY, ERROR_TEXT_KEY, HIDE_BUTTON_TEXT_KEY, ITEM_REMOVED_ANNOUNCER_TEXT_KEY, MODAL_TITLE_KEY, POPUP_ARIA_LABEL_KEY, REGION_UPDATED_ANNOUNCER_TEXT_KEY, REMOVE_BUTTON_TEXT_KEY, REMOVE_LINK_TEXT_KEY, SHOW_BUTTON_TEXT_KEY, MAX_PRODUCTS_TO_COMPARE, COMPONENT_NAME } from "../constants.js";
import { getCompareProductsDisplayUrl } from "../lib.js";
import { useLocalStorage } from "../../../../hooks/useLocalStorage/index.js";
import { ActionType } from "./types.js";
import { StyledContainer, StyledRowContainer, StyledProductsContainer, StyledButtonsContainer } from "./styles.js";
const initialState = {
  compareProductsError: false
};
function reducer(state, action) {
  switch (action.type) {
    case ActionType.setCompareProductsError:
      return { ...state, compareProductsError: action.payload };
    case ActionType.setAnnouncerText:
      return { ...state, announcerText: action.payload };
    default:
      return state;
  }
}
const CompareProductsUI = ({
  moduleSettings,
  popupHiddenLocalStorageKey,
  productsLocalStorageKey
}) => {
  const [compareProductsState, setCompareProductsState] = useLocalStorage(
    productsLocalStorageKey,
    []
  );
  const [popupHiddenState, setPopupHiddenState] = useLocalStorage(
    popupHiddenLocalStorageKey,
    false
  );
  const contentRef = useRef(null);
  const errorRef = useRef(null);
  const msStore = useMemo(
    () => new ModuleSettingsStore({
      composable_window: [],
      key_value: moduleSettings.key_value
    }),
    [moduleSettings]
  );
  const allItemsRemovedAnnouncerText = msStore.getKeyValueItem(
    ALL_ITEMS_REMOVED_ANNOUNCER_TEXT_KEY
  );
  const compareButtonText = msStore.getKeyValueItem(COMPARE_BUTTON_TEXT_KEY);
  const emptySlotTemplateText = msStore.getKeyValueItem(EMPTY_SLOT_TEXT_KEY);
  const errorText = msStore.getKeyValueItem(ERROR_TEXT_KEY);
  const hideButtonText = msStore.getKeyValueItem(HIDE_BUTTON_TEXT_KEY);
  const itemRemovedAnnouncerText = msStore.getKeyValueItem(
    ITEM_REMOVED_ANNOUNCER_TEXT_KEY
  );
  const modelTitleText = msStore.getKeyValueItem(MODAL_TITLE_KEY);
  const popupAriaLabel = msStore.getKeyValueItem(POPUP_ARIA_LABEL_KEY);
  const regionUpdatedAnnouncerText = msStore.getKeyValueItem(
    REGION_UPDATED_ANNOUNCER_TEXT_KEY
  );
  const removeButtonText = msStore.getKeyValueItem(REMOVE_BUTTON_TEXT_KEY);
  const removeLinkText = msStore.getKeyValueItem(REMOVE_LINK_TEXT_KEY);
  const showButtonText = msStore.getKeyValueItem(SHOW_BUTTON_TEXT_KEY);
  const properties = moduleSettings.properties[0];
  const compareProductsDisplayBaseUrl = properties == null ? void 0 : properties.compareProductsDisplayURL;
  const noImageUrl = properties == null ? void 0 : properties.noImgURL;
  const [compareProductsLength, setCompareProductsLength] = useState(0);
  const [{ compareProductsError, announcerText }, dispatch] = useReducer(reducer, {
    ...initialState,
    announcerText: regionUpdatedAnnouncerText
  });
  const isOpen = (compareProductsState == null ? void 0 : compareProductsState.length) && compareProductsState.length > 0;
  const onRemoveItem = async (partNumber) => {
    var _a;
    const updatedProducts = compareProductsState == null ? void 0 : compareProductsState.filter(
      (item) => item.partNumber !== partNumber
    );
    if (updatedProducts) {
      await setCompareProductsState(updatedProducts);
    }
    if (compareProductsError) {
      dispatch({
        payload: false,
        type: ActionType.setCompareProductsError
      });
    }
    dispatch({
      payload: itemRemovedAnnouncerText,
      type: ActionType.setAnnouncerText
    });
    (_a = contentRef.current) == null ? void 0 : _a.focus();
  };
  const handleRemoveAll = async () => {
    await setCompareProductsState([]);
    if (compareProductsError) {
      dispatch({
        payload: false,
        type: ActionType.setCompareProductsError
      });
    }
    dispatch({
      payload: allItemsRemovedAnnouncerText,
      type: ActionType.setAnnouncerText
    });
  };
  const handleHide = async () => {
    await setPopupHiddenState(true);
  };
  const handleShow = async () => {
    await setPopupHiddenState(false);
  };
  const compareProductsDisplayFullUrl = getCompareProductsDisplayUrl(
    compareProductsDisplayBaseUrl,
    compareProductsState || []
  );
  useEffect(() => {
    const asyncFn = async () => {
      if (compareProductsState && compareProductsState.length > MAX_PRODUCTS_TO_COMPARE) {
        dispatch({
          payload: true,
          type: ActionType.setCompareProductsError
        });
        await setCompareProductsState(
          compareProductsState.slice(0, MAX_PRODUCTS_TO_COMPARE)
        );
      }
    };
    asyncFn();
  }, [compareProductsState, setCompareProductsState]);
  useEffect(() => {
    var _a;
    if (compareProductsError) {
      (_a = errorRef.current) == null ? void 0 : _a.focus();
      dispatch({
        payload: errorText,
        type: ActionType.setAnnouncerText
      });
    }
  }, [compareProductsError, errorText]);
  useEffect(() => {
    if (compareProductsError && compareProductsState && compareProductsState.length < MAX_PRODUCTS_TO_COMPARE) {
      dispatch({
        payload: false,
        type: ActionType.setCompareProductsError
      });
    }
  }, [compareProductsError, compareProductsState]);
  useEffect(() => {
    if (compareProductsState && compareProductsState.length > 0 && compareProductsState.length <= MAX_PRODUCTS_TO_COMPARE && compareProductsState.length > compareProductsLength) {
      dispatch({
        payload: regionUpdatedAnnouncerText,
        type: ActionType.setAnnouncerText
      });
    }
    if (compareProductsState) {
      setCompareProductsLength(compareProductsState.length);
    }
  }, [
    compareProductsState,
    compareProductsLength,
    regionUpdatedAnnouncerText
  ]);
  return /* @__PURE__ */ jsx(
    Popup,
    {
      announcer: /* @__PURE__ */ jsx(
        AnnouncerModel,
        {
          callback: () => {
            dispatch({
              payload: "",
              type: ActionType.setAnnouncerText
            });
          },
          announcerText,
          announcerTime: 2e3,
          ariaLive: "assertive"
        }
      ),
      renderContent: () => /* @__PURE__ */ jsxs(StyledContainer, { children: [
        compareProductsError && /* @__PURE__ */ jsx(
          Notification,
          {
            isRole: true,
            message: errorText,
            ref: errorRef,
            severity: "critical",
            sx: { lineHeight: "1.5" }
          }
        ),
        /* @__PURE__ */ jsxs(StyledRowContainer, { children: [
          /* @__PURE__ */ jsx(StyledProductsContainer, { children: Array.from(
            { length: MAX_PRODUCTS_TO_COMPARE },
            (_, i) => /* @__PURE__ */ jsx(
              CompareProductTile,
              {
                emptySlotTemplateText,
                index: i,
                noImageUrl,
                onRemove: onRemoveItem,
                product: compareProductsState == null ? void 0 : compareProductsState[i],
                removeButtonText: removeLinkText
              },
              i
            )
          ) }),
          /* @__PURE__ */ jsxs(StyledButtonsContainer, { children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                href: compareProductsDisplayFullUrl,
                sx: { lineHeight: FontLineHeightMd },
                variant: "primary",
                children: compareButtonText
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                onClick: handleRemoveAll,
                sx: {
                  lineHeight: FontLineHeightMd,
                  paddingTop: SpaceSm
                },
                variant: "text-button",
                children: removeButtonText
              }
            )
          ] })
        ] })
      ] }),
      contentRef,
      "data-testid": COMPONENT_NAME,
      defaultExpanded: !popupHiddenState,
      enableEntryAnimation: true,
      hideButtonId: "hideButtonId",
      hideLabelText: hideButtonText,
      isCloseable: false,
      onHide: handleHide,
      onShow: handleShow,
      open: isOpen,
      popupAriaLabel,
      popupName: "compareProducts",
      position: "Right",
      showButtonId: "showButtonId",
      showLabelText: showButtonText,
      strategy: "absolute",
      title: modelTitleText,
      titleId: `${COMPONENT_NAME}-title`
    }
  );
};
CompareProductsUI.displayName = COMPONENT_NAME;
export {
  CompareProductsUI
};
